/**
 * The other end of an invite.
 *
 * Two paths arrive here and only one of them is the easy one. A guardian who is
 * already signed in follows the link, joins, and lands on the family's home
 * screen with no screen in between — they followed a link in order to do this,
 * so asking them to confirm it would be a dialog with one sensible answer.
 *
 * The second path is the one that shapes this file: a guardian with no account.
 * Signing in is a magic link, and a magic link comes back to the origin rather
 * than to `/join`, so the code has to be stashed before the auth gate (see
 * App.jsx) and picked up again wherever the mail happens to land. That is what
 * `ApplyPendingInvite` is for, and why the stash is cleared the moment this
 * screen has the code in hand — a code that fails must not send every other
 * screen in the app back here for ever.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useFamily } from '../lib/family.jsx';
import { forgetInviteCode, normalizeInviteCode, pendingInviteCode } from '../lib/invite.js';

/**
 * Wraps the router: while a code is waiting, joining is what the app is for.
 *
 * The alternative — applying it quietly from the provider — would join a family
 * as a side effect of loading a screen, with nothing on screen saying so. This
 * way the redemption always happens on a page that is about redeeming it.
 */
export function ApplyPendingInvite({ children }) {
  const { pathname } = useLocation();
  const pending = pendingInviteCode();
  if (pending && pathname !== '/join') {
    return <Navigate to={`/join?code=${encodeURIComponent(pending)}`} replace />;
  }
  return children;
}

export default function Join() {
  const { acceptInvite, family } = useFamily();
  const [searchParams] = useSearchParams();
  // Read once, on mount: the stash is cleared below, so re-reading it on a
  // later render would turn a working code into a missing one.
  const [code] = useState(() => normalizeInviteCode(searchParams.get('code')) ?? pendingInviteCode());
  const [status, setStatus] = useState(code ? 'ready' : 'nocode');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { forgetInviteCode(); }, []);

  const join = useCallback(async () => {
    setStatus('joining');
    setError(null);
    try {
      await acceptInvite(code);
      navigate('/', { replace: true });
    } catch (e) {
      setError(e.message);
      setStatus('failed');
    }
  }, [code, acceptInvite, navigate]);

  // The ordinary case: a second parent with no family of their own. Nothing to
  // ask, so nothing is asked.
  useEffect(() => {
    if (status === 'ready' && !family) join();
  }, [status, family, join]);

  if (status === 'nocode') {
    return (
      <main className="app-shell stack">
        <h1>That link is incomplete</h1>
        <p className="muted">
          The invite code is missing from it. Ask whoever sent it to copy the whole link,
          or to create a new one — they expire after a week either way.
        </p>
        <Link to="/"><button className="quiet">Go to Fortunaut</button></Link>
      </main>
    );
  }

  if (status === 'failed') {
    return (
      <main className="app-shell stack">
        <h1>That invite didn’t work</h1>
        {/* The reason comes from the database and is written to be read by a
            parent: not valid, already used, or expired. Each is fixed by the
            same thing — a fresh link — so say so rather than guessing which. */}
        <p className="notice error">{error}</p>
        <p className="muted">
          Ask whoever invited you to create a new link. Codes are single-use and last
          seven days, so an old one in a chat thread will always look like this.
        </p>
        <Link to="/"><button className="quiet">Go to Fortunaut</button></Link>
      </main>
    );
  }

  // Already in a family. This app shows one family at a time, and nothing here
  // can tell them whose invite this is — reading an invite you have not
  // redeemed is exactly what the policies forbid — so it says what it knows.
  if (status === 'ready' && family) {
    return (
      <main className="app-shell stack">
        <p className="eyebrow">Invitation</p>
        <h1>You’re already in {family.name}</h1>
        <p className="muted">
          If this link is for {family.name}, accepting it changes nothing. If it is for a
          different family, you will belong to both and Fortunaut will show one of them —
          which is probably not what you want.
        </p>
        <div className="row">
          <button className="primary" onClick={join}>Accept anyway</button>
          <Link to="/"><button className="quiet">Stay where I am</button></Link>
        </div>
      </main>
    );
  }

  return (
    <main className="app-shell stack">
      <p className="eyebrow">Invitation</p>
      <h1>Joining the family…</h1>
      <p className="muted" role="status">One moment.</p>
    </main>
  );
}
